import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Progress,
  DatePicker,
  Select,
  Statistic,
  Tag,
  Button,
  Spin,
  Empty,
  Space,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { getCapacityUtilization, getAllMachines } from "../../api/schedulingApi";
import checkTokenExp from "../../utils/checkTokenExp";

const { RangePicker } = DatePicker;

/**
 * CapacityDashboard — machine utilization overview.
 *
 * API Guide §7.9  GET /v1/production/schedule/capacity
 * Response shape:
 *  { period: { start, end },
 *    utilization: [{ machine_id, machine_name, machine_code,
 *      scheduled_minutes, available_minutes, utilization_pct,
 *      schedule_count, status }] }
 */
const CapacityDashboard = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [capacityData, setCapacityData] = useState([]);
  const [machines, setMachines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("week"),
    dayjs().endOf("week"),
  ]);

  // ── Fetch ──────────────────────────────────────────────────
  useEffect(() => {
    fetchMachines();
  }, []);

  useEffect(() => {
    if (dateRange[0] && dateRange[1]) fetchCapacity();
  }, [dateRange, selectedMachines]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to="/auth/login" replace />;
  }
  checkTokenExp(accessToken);

  const fetchMachines = async () => {
    try {
      const data = await getAllMachines({ status: "operational" });
      setMachines(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error("Failed to load machines", error);
    }
  };

  const fetchCapacity = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: dateRange[0].format("YYYY-MM-DD"),
        end_date: dateRange[1].format("YYYY-MM-DD"),
      };
      if (selectedMachines.length > 0) {
        params.machine_ids = selectedMachines.join(",");
      }
      const data = await getCapacityUtilization(params);
      setCapacityData(data.utilization || []);
    } catch (error) {
      toast.error("Failed to load capacity data");
    }
    setLoading(false);
  };

  // ── Helpers ────────────────────────────────────────────────
  const getStatusColor = (status) => {
    switch (status) {
      case "overloaded":
        return "#ff4d4f";
      case "high":
        return "#faad14";
      case "normal":
        return "#52c41a";
      default:
        return "#1890ff";
    }
  };

  const getProgressStatus = (status) => {
    switch (status) {
      case "overloaded":
        return "exception";
      case "high":
        return "active";
      default:
        return "success";
    }
  };

  const formatHours = (minutes) => {
    if (!minutes && minutes !== 0) return "-";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <Card
      title="Machine Capacity Utilization"
      extra={
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(val) => val && setDateRange(val)}
            format="DD MMM YYYY"
            size="small"
          />
          <Select
            mode="multiple"
            placeholder="Filter machines"
            style={{ minWidth: 180 }}
            value={selectedMachines}
            onChange={setSelectedMachines}
            allowClear
            maxTagCount={2}
            size="small"
          >
            {machines.map((m) => (
              <Select.Option key={m.id} value={m.id}>
                {m.name}
              </Select.Option>
            ))}
          </Select>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchCapacity}
            loading={loading}
            size="small"
          >
            Refresh
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          {capacityData.map((machine) => (
            <Col xs={24} sm={12} md={8} lg={6} key={machine.machine_id}>
              <Card
                size="small"
                hoverable
                style={{
                  borderTop: `3px solid ${getStatusColor(machine.status)}`,
                }}
              >
                <Statistic
                  title={
                    <span style={{ fontWeight: 600 }}>
                      {machine.machine_name}
                    </span>
                  }
                  value={machine.utilization_pct}
                  suffix="%"
                  valueStyle={{
                    color: getStatusColor(machine.status),
                    fontSize: 28,
                  }}
                />
                <div style={{ margin: "8px 0" }}>
                  <Progress
                    percent={Math.min(machine.utilization_pct, 100)}
                    status={getProgressStatus(machine.status)}
                    showInfo={false}
                    size="small"
                    strokeColor={getStatusColor(machine.status)}
                  />
                </div>
                <div style={{ fontSize: 12, color: "#888", lineHeight: 1.8 }}>
                  <div>
                    Scheduled: <strong>{formatHours(machine.scheduled_minutes)}</strong>
                  </div>
                  <div>
                    Available: <strong>{formatHours(machine.available_minutes)}</strong>
                  </div>
                  <div>
                    Jobs: <strong>{machine.schedule_count || 0}</strong>
                  </div>
                </div>
                <Tag
                  color={getStatusColor(machine.status)}
                  style={{ marginTop: 8 }}
                >
                  {(machine.status || "unknown").toUpperCase()}
                </Tag>
                <span style={{ fontSize: 11, color: "#aaa", marginLeft: 8 }}>
                  {machine.machine_code}
                </span>
              </Card>
            </Col>
          ))}
        </Row>

        {capacityData.length === 0 && !loading && (
          <Empty
            description="No capacity data available for the selected period"
            style={{ padding: 48 }}
          />
        )}
      </Spin>
    </Card>
  );
};

export default CapacityDashboard;
